/* eslint-disable sort-keys */
import {
    buttonIcon,
    buttonInner, buttonWrapper,
    link,
    linkText,
} from './styles'

import Button from 'components/Button'
import {ButtonProps} from 'components/Button/types'
import {ExternalLinkIcon} from 'components/Icon'
import {Icon} from 'components/Icon/types'
import React from 'react'

import {config} from 'embed/listeners'
import {styled} from 'stitches/config'

const StyledQuestionnaireButton = styled('div', buttonWrapper)
const StyledQuestionnaireButtonInner = styled('div', buttonInner)
const StyledIcon = styled('div', buttonIcon)
const StyledLink = styled('a', link)
const StyledLinkText = styled('span', linkText)

export interface QuestionnaireButtonProps {
    delay?: number
    lightBot?: boolean
    currentElement?: boolean
    align?: 'right' | 'center' | 'left'
    clearAfter?: boolean
    disabled?: boolean
    variant?: 'primary' | 'secondary' | 'tertiary',
    hide?: boolean
    text?: string
    textAlign?: 'left' | 'right' | 'center'
    maxWidth?: string
    margin?: string
    target?: string
    padding?: string
    inline?: boolean
    iconLeft?: {
        src?: string
        svg?: string
    }
    iconRight?: {
        src?: string
        svg?: string
    }
    href?: string
    onClick: () => void
}

export const QuestionnaireButton = (props: QuestionnaireButtonProps) => {
    const {
        align, variant, text, onClick, textAlign, clearAfter,
        inline, iconLeft, iconRight, href, hide, target,
        maxWidth, margin, padding, disabled,
        delay, currentElement, lightBot,
        ...other
    } = props
    const targetOrigin = config.get('targetOrigin') || ''

    const linkTarget = () => {
        if (target) {
            return target
        }

        if (window.location.origin === targetOrigin || window.location.origin === 'https://ninchat.com') {
            return '_self'
        }

        return '_blank'
    }


    const handleButtonPress = () => {
        onClick()
    }

    const iconLeftSelected = () => {
        if (iconLeft?.svg) {
            // eslint-disable-next-line react/no-danger
            return <StyledIcon aria-hidden='true' dangerouslySetInnerHTML={{__html: iconLeft?.svg}} />
        } else if (iconLeft?.src) {
            return <img aria-hidden='true' src={iconLeft?.src} />
        }
        return <div />
    }

    const iconRightSelected = () => {
        if (href) {
            return <ExternalLinkIcon />
        } else if (iconRight?.svg) {
            // eslint-disable-next-line react/no-danger
            return <StyledIcon aria-hidden='true' dangerouslySetInnerHTML={{__html: iconRight?.svg}} />
        } else if (iconRight?.src) {
            return <img aria-hidden='true' src={iconRight?.src} />
        }
        return <div />
    }


    // eslint-disable-next-line no-undef-init
    let color: ButtonProps['color'] = undefined
    if (variant === 'primary') {
        color = 'filled'
    } else if (variant === 'tertiary') {
        color = 'text'
    }

    const isExternalLink = linkTarget() === '_blank'
    const accessibleText = isExternalLink && href
        ? `${text} (opens in new window)`
        : text

    return (
        <StyledQuestionnaireButton
            align={align}
            clearAfter={clearAfter}
            css={{
                $$delay: `${delay}s`,
                margin,
            }}
            currentElement={currentElement}
            hide={hide}
            inline={inline}
            lightBot={lightBot}
            textAlign={textAlign}
        >
            <StyledQuestionnaireButtonInner css={{maxWidth}} >
                {href
                    ? (
                        <StyledLink
                            aria-label={isExternalLink ? accessibleText : undefined}
                            color={color}
                            css={{'&&': {padding}}}
                            href={href}
                            rel={isExternalLink ? accessibleText : undefined}
                            target={linkTarget()}
                            {...other}

                        >
                            {iconLeftSelected()}
                            <StyledLinkText color={color} disabled={disabled} multiline={true}>{text}</StyledLinkText>
                            {iconRightSelected()}
                        </StyledLink>
                    )
                    :
                    (
                        <Button
                            aria-label={isExternalLink ? accessibleText : undefined}
                            color={color}
                            css={{'&&': {padding}}}
                            disabled={disabled}
                            iconLeft={iconLeftSelected as unknown as Icon}
                            iconRight={iconRightSelected as unknown as Icon}
                            multiline={true}
                            onPress={handleButtonPress}
                            text={text}
                            {...other}
                        />)}

            </StyledQuestionnaireButtonInner>
        </StyledQuestionnaireButton>
    )
}

QuestionnaireButton.defaultProps = {
    align: '',
    clearAfter: false,
    currentElement: false,
    delay: 0,
    disabled: false,
    hide: false,
    href: '',
    iconLeft: {},
    iconRight: {},
    inline: false,
    lightBot: false,
    margin: '',
    maxWidth: '',
    padding: '',
    target: '',
    text: '',
    textAlign: '',
    variant: 'secondary',
}
